// Depth of field.
//
// The map is drawn to a texture instead of the screen, and then put back through
// a blur whose radius grows with distance from whatever is locked. Everything
// near the target stays sharp and the rest of the estate softens off, which is
// what makes a selection read as a selection rather than as a coloured ring.
//
// Deliberately not multisampled. The renderer already draws into a backing store
// larger than the viewport, so edges survive the round trip well enough, and a
// multisampled target would need a resolve blit on every frame to gain very
// little under a blur.

const VERTEX = `
attribute vec2 aPos;
varying vec2 vUV;

void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAGMENT = `
precision highp float;

uniform sampler2D uScene;

// Centre of the sharp region in texture space, its radius as a fraction of the
// viewport's height, and the viewport's aspect — so a circular focus stays
// circular on a wide screen. uTexel is the size of one texel, which is what the
// blur offsets are measured in.
uniform vec2 uFocus;
uniform float uFocusRadius;
uniform float uAspect;
uniform vec2 uTexel;
uniform float uStrength;

varying vec2 vUV;

// A disc of taps rather than a separable pass, so the whole effect stays a
// single pass over one texture.
//
// Laid out on a golden-angle spiral rather than on fixed rings: the taps come
// out at even density across the disc, so a wide blur fills in instead of
// banding at the radii the rings sat at.
const int TAPS = 28;
const float GOLDEN_ANGLE = 2.39996323;

void main() {
  vec2 d = vUV - uFocus;
  d.x *= uAspect;
  float dist = length(d);

  // Sharp inside the target and easing out across most of the viewport, so what
  // is being looked at keeps its surroundings for context. Reaching full blur
  // just outside the target reads as fog rather than as focus.
  float blur = smoothstep(uFocusRadius, uFocusRadius + 0.55, dist);
  float radius = blur * uStrength;

  if (radius < 0.0015) {
    gl_FragColor = texture2D(uScene, vUV);
    return;
  }

  vec4 sum = texture2D(uScene, vUV);
  for (int i = 0; i < TAPS; i++) {
    float fi = float(i);
    float a = fi * GOLDEN_ANGLE;
    // Square root, because a linear radius would crowd the taps into the middle
    // and leave the rim of the disc undersampled.
    float scale = sqrt((fi + 0.5) / float(TAPS));
    vec2 offset = vec2(cos(a), sin(a)) * radius * scale * vec2(1.0, uAspect);
    sum += texture2D(uScene, vUV + offset);
  }

  gl_FragColor = sum / float(TAPS + 1);
}
`;

// The furthest anything is pushed, as a fraction of the viewport's height. Enough
// to read as out of focus, not so much that the rest of the estate stops being
// recognisable — the point is to direct attention, not to hide everything else.
const MAX_BLUR = 0.0025;

// How many samples to ask for when the context can multisample an offscreen
// target. Four is where the returns fall away on the hairlines this is protecting,
// and the driver clamps to what it actually supports anyway.
const MAX_SAMPLES = 4;

export class DepthOfField {
  constructor(gl) {
    this.gl = gl;
    this.width = 0;
    this.height = 0;

    // The canvas is created with `antialias: true`, but in WebGL that only ever
    // applied to the default framebuffer — drawing the scene into a texture to
    // blur it threw the multisampling away, and every hairline on the map went
    // ragged the moment the effect came on.
    //
    // WebGL2 can multisample a renderbuffer and resolve it with a blit, so the
    // scene goes into that and is resolved into the texture the blur reads.
    // WebGL1 has no equivalent and keeps the plain single-sampled path.
    this.multisampled = typeof gl.blitFramebuffer === "function";

    this.framebuffer = gl.createFramebuffer();
    this.texture = gl.createTexture();
    this.depth = gl.createRenderbuffer();

    if (this.multisampled) {
      this.msFramebuffer = gl.createFramebuffer();
      this.msColour = gl.createRenderbuffer();
      this.msDepth = gl.createRenderbuffer();
    }

    this.program = build(gl);
    this.quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    );
  }

  resize(width, height) {
    if (width === this.width && height === this.height) return;
    this.width = width;
    this.height = height;

    const gl = this.gl;

    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      width,
      height,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.texture,
      0
    );

    // Stencil as well as depth: the clipping the map does for rounded tiles and
    // the dial's meters is a stencil test, and it has nowhere to go otherwise.
    // It belongs to whichever target the scene is actually drawn into.
    if (!this.multisampled) {
      gl.bindRenderbuffer(gl.RENDERBUFFER, this.depth);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_STENCIL, width, height);
      gl.framebufferRenderbuffer(
        gl.FRAMEBUFFER,
        gl.DEPTH_STENCIL_ATTACHMENT,
        gl.RENDERBUFFER,
        this.depth
      );
    }

    this.complete =
      gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;

    if (this.multisampled) {
      const samples = Math.min(MAX_SAMPLES, gl.getParameter(gl.MAX_SAMPLES));

      gl.bindRenderbuffer(gl.RENDERBUFFER, this.msColour);
      gl.renderbufferStorageMultisample(
        gl.RENDERBUFFER,
        samples,
        gl.RGBA8,
        width,
        height
      );

      gl.bindRenderbuffer(gl.RENDERBUFFER, this.msDepth);
      gl.renderbufferStorageMultisample(
        gl.RENDERBUFFER,
        samples,
        gl.DEPTH24_STENCIL8,
        width,
        height
      );

      gl.bindFramebuffer(gl.FRAMEBUFFER, this.msFramebuffer);
      gl.framebufferRenderbuffer(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.RENDERBUFFER,
        this.msColour
      );
      gl.framebufferRenderbuffer(
        gl.FRAMEBUFFER,
        gl.DEPTH_STENCIL_ATTACHMENT,
        gl.RENDERBUFFER,
        this.msDepth
      );

      // Both have to be usable: the scene is drawn into one and resolved into
      // the other, so losing either means falling back to drawing direct.
      this.complete =
        this.complete &&
        gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);
  }

  begin(width, height) {
    this.resize(width, height);
    if (!this.complete) return false;

    const gl = this.gl;
    gl.bindFramebuffer(
      gl.FRAMEBUFFER,
      this.multisampled ? this.msFramebuffer : this.framebuffer
    );
    return true;
  }

  // `focus` is in view pixels with the origin at the middle of the viewport,
  // which is where everything else in the overlay lives.
  end(focus, viewWidth, viewHeight, amount) {
    const gl = this.gl;

    // Resolve the multisampled scene down into the texture the blur samples.
    if (this.multisampled) {
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.msFramebuffer);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.framebuffer);
      gl.blitFramebuffer(
        0,
        0,
        this.width,
        this.height,
        0,
        0,
        this.width,
        this.height,
        gl.COLOR_BUFFER_BIT,
        gl.NEAREST
      );
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    gl.useProgram(this.program.program);
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.BLEND);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.uniform1i(this.program.scene, 0);

    // The focus arrives in view pixels about the middle of the viewport, which is
    // where the overlay works; the texture is in drawing-buffer pixels. Only the
    // fraction matters, so the two never have to be reconciled.
    gl.uniform2f(
      this.program.focus,
      focus.x / viewWidth + 0.5,
      // Flipped: the overlay measures y downwards and the texture measures v
      // upwards, so adding here put the sharp region on the wrong side of the map.
      0.5 - focus.y / viewHeight
    );
    gl.uniform1f(this.program.focusRadius, focus.r / viewHeight);
    gl.uniform1f(this.program.aspect, viewWidth / viewHeight);
    gl.uniform2f(this.program.texel, 1 / this.width, 1 / this.height);
    gl.uniform1f(this.program.strength, MAX_BLUR * amount);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
    gl.enableVertexAttribArray(this.program.position);
    gl.vertexAttribPointer(this.program.position, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    gl.enable(gl.BLEND);
  }
}

function build(gl) {
  const compile = (type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error("depth of field shader failed: " + log);
    }
    return shader;
  };

  const program = gl.createProgram();
  gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX));
  gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(
      "depth of field link failed: " + gl.getProgramInfoLog(program)
    );
  }

  return {
    program,
    position: gl.getAttribLocation(program, "aPos"),
    scene: gl.getUniformLocation(program, "uScene"),
    focus: gl.getUniformLocation(program, "uFocus"),
    focusRadius: gl.getUniformLocation(program, "uFocusRadius"),
    aspect: gl.getUniformLocation(program, "uAspect"),
    texel: gl.getUniformLocation(program, "uTexel"),
    strength: gl.getUniformLocation(program, "uStrength"),
  };
}
